import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';
import { XCircleIcon } from '@phosphor-icons/react/dist/csr/XCircle';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { replacementsApi, type ReplacementRow } from '../../lib/api/replacements';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './RejectDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

/**
 * `POST /replacements/{replacement}/reject` (M-13, Task 5, spec R-3).
 *
 * ## The reason is required, and that is the whole difference from the legacy
 *
 * `admin-show-active-replacements.php:51` runs
 * `UPDATE replacements SET Status = 'dismissed'` from `$_GET["act"]` — on a
 * GET, with the uuid interpolated into a string that is then handed to
 * `prepare()`, which is prepared-statement syntax with no bound parameters
 * and therefore decoration rather than protection. It recorded a patient
 * being refused a device and said nothing whatsoever about why. The reason
 * is the only thing that makes this a different act.
 *
 * The blank check runs HERE and on the server twice over
 * (`RejectReplacementRequest` and
 * `ReplacementTransition::reject()` itself) — see that exception's own
 * docblock for why the service holds the rule too.
 *
 * ## The server's refusal is not always keyed to `rejection_reason`
 *
 * A `409` for "already dispensed" or "already rejected" names `status`,
 * never `rejection_reason` — so it goes to the failure banner rather than
 * being silently dropped, the identical split `AnswerDialog.tsx`'s own
 * docblock explains for its own case. That `409` is reachable in normal
 * use: two pharmacists can open the same queue.
 */

export interface RejectDialogProps {
  open: boolean;
  /** `null` while nothing is being acted on — this dialog is not mounted with `open` in that state, but the type still allows it so a caller cannot pass a stale uuid by construction. */
  replacementUuid: string | null;
  /** The replacement's own case number, so the dialog's title names the record rather than asking a bare "Are you sure?" (`Dialog`'s own development guard). */
  caseNumber: string;
  onClose: () => void;
  onDone: (updated: ReplacementRow) => void;
}

export function RejectDialog({ open, replacementUuid, caseNumber, onClose, onDone }: RejectDialogProps) {
  const { t } = useTranslation();

  const [text, setText] = useState('');
  const [missingText, setMissingText] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setText('');
    setMissingText(false);
    setFailure(null);
    setFieldErrors({});
  }, [open]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('replacements.notPermittedToFulfil');
      case TOO_MANY_REQUESTS_CODE:
        return t('replacements.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('replacements.rejectFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current || replacementUuid === null) return;

    if (text.trim() === '') {
      setMissingText(true);
      return;
    }
    setMissingText(false);

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setFieldErrors({});

    try {
      const updated = await replacementsApi.reject(replacementUuid, { rejection_reason: text.trim() });
      onDone(updated);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      const mapped = mapDetails(error.details);

      // THE SERVER'S REFUSAL IS NOT ALWAYS KEYED TO `rejection_reason` — see
      // this file's own docblock. A `409`'s `status` key is neither, so it
      // surfaces in the banner instead of being dropped.
      const banner = Object.entries(mapped)
        .filter(([field]) => field !== 'rejection_reason')
        .map(([, message]) => message);

      setFieldErrors(mapped);

      if (banner.length > 0) {
        setFailure(banner.join(' '));
      } else if (Object.keys(mapped).length === 0) {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} kind="form" onClose={onClose} title={t('replacements.rejectTitle', { caseNumber })}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <Textarea
          label={t('replacements.rejectReasonLabel')}
          dir="auto"
          value={text}
          error={missingText ? t('replacements.required') : fieldErrors['rejection_reason']}
          onChange={(event) => {
            const { value } = event.target;
            setText(value);
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<XCircleIcon size={16} />}>
            {t('replacements.rejectSubmit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('replacements.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
